import React from "react";

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatDateTime = (dateStr: string | null) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const LANG_NAMES: Record<string, string> = {
  en: "English", fr: "French", de: "German", es: "Spanish", it: "Italian", zh: "Chinese",
  hi: "Hindi", ru: "Russian", nl: "Dutch", pt: "Portuguese", pl: "Polish", cs: "Czech",
  sv: "Swedish", ta: "Tamil", ja: "Japanese", ko: "Korean", da: "Danish", el: "Greek", gu: "Gujarati",
};
const langName = (code: string | null | undefined) => (code ? LANG_NAMES[code] || code : null);

// Language provenance written by the booking importer (plan D7). Older rows
// were labelled "legacy_unverified" by migration 001 and are shown as such,
// never as a known language.
const LanguageStatusPill = ({ status }: { status: string | null | undefined }) => {
  switch (status) {
    case "known":
      return <span className="px-2 py-0.5 text-[11px] font-semibold bg-green-100 text-green-700 rounded-full">Known</span>;
    case "conflict":
      return <span className="px-2 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-800 rounded-full" title="The booking carries two different languages">Conflict</span>;
    case "unknown":
      return <span className="px-2 py-0.5 text-[11px] font-semibold bg-gray-100 text-gray-600 rounded-full" title="No language evidence on this booking">Unknown</span>;
    default:
      return <span className="px-2 py-0.5 text-[11px] font-semibold bg-gray-100 text-gray-600 rounded-full" title="Imported before language checking existed (7 Sep 2026)">Not verified</span>;
  }
};

// Outcome of the tour-code e-mail from the e-mail record book (plan D8).
// "Accepted" means our mail server took it, not that it was delivered.
const EmailStatusPill = ({ attempt }: { attempt: any }) => {
  if (!attempt) {
    return <span className="px-2 py-0.5 text-[11px] font-semibold bg-gray-100 text-gray-600 rounded-full" title="Sent before the e-mail record book started (7 Sep 2026)">Not recorded</span>;
  }
  switch (attempt.status) {
    case "accepted":
      return <span className="px-2 py-0.5 text-[11px] font-semibold bg-green-100 text-green-700 rounded-full" title={`Accepted by the mail server ${formatDateTime(attempt.updatedAt || attempt.createdAt)}`}>Accepted</span>;
    case "failed":
      return <span className="px-2 py-0.5 text-[11px] font-semibold bg-red-100 text-red-700 rounded-full" title={attempt.lastError || "The mail server refused it"}>Failed</span>;
    case "unknown_outcome":
      return <span className="px-2 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-800 rounded-full" title="The mail server never answered; resend manually if the customer did not get it">Unknown outcome</span>;
    case "pending":
      return <span className="px-2 py-0.5 text-[11px] font-semibold bg-blue-100 text-blue-700 rounded-full">Sending…</span>;
    default:
      return <span className="px-2 py-0.5 text-[11px] font-semibold bg-gray-100 text-gray-600 rounded-full">{attempt.status}</span>;
  }
};

const BookingsTableComponent = ({ allUser, onResend, resendingId }: any) => {

  return (
    <>
      <div className="overflow-x-auto">
      <table className="w-full text-sm text-left h-40 mb-9 min-w-[1500px]">
        <thead className="text-xs font-Roboto font-normal text-lightgray">
          <tr>
            <th scope="col" className="p-2">
              <div className="flex items-center"></div>
            </th>
            <th scope="col" className="p-2">BookingId</th>
            <th scope="col" className="p-2">EXT Booking Ref</th>
            <th scope="col" className="p-2">Name</th>
            <th scope="col" className="p-2">Email</th>
            <th scope="col" className="p-2">Phone No.</th>
            <th scope="col" className="p-2">Tour</th>
            <th scope="col" className="p-2">Total Paid</th>
            <th scope="col" className="p-2">Redeem Code</th>
            <th scope="col" className="p-2">Redeems Left</th>
            <th scope="col" className="p-2">Participants</th>
            <th scope="col" className="p-2">Language</th>
            <th scope="col" className="p-2">Code E-mail</th>
            <th scope="col" className="p-2">Booking Date</th>
            <th scope="col" className="p-2">Created</th>
            <th scope="col" className="p-2">Notification</th>
            <th scope="col" className="p-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {allUser &&
            allUser?.map((elm: any, index: any) => {
              const isCancelled = elm?.status === 0;
              const cancellationCount = elm?.cancellationCount || 0;
              const isRepeatCanceller = cancellationCount >= 2;
              const attempt = elm?.emailAttempt || null;
              const commLanguage = langName(elm?.commLanguage);
              const audioLanguage = langName(elm?.audioLanguage);
              const emailLanguage = attempt?.renderedLanguage ? langName(attempt.renderedLanguage) : null;
              const canResend = !!elm?.email && !isCancelled && typeof onResend === "function";
              const isResending = resendingId === elm?.bookingId;
              return (
              <tr
                key={index}
                className={`border-b border-aqua ${isCancelled ? "bg-red-50" : "bg-white"}`}
              >
                <td className="w-4 p-2">
                  <div className="flex items-center"></div>
                </td>
                <td className="p-2">{elm.bookingId}</td>
                <td className="p-2 text-xs">{elm?.bokunExternalRef || elm?.externalBookingRef || "—"}</td>
                <td className="p-2">
                  {elm.name}
                  {isRepeatCanceller && (
                    <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded" title={`${cancellationCount} cancelled bookings from this email`}>
                      {cancellationCount}x CANCELLED
                    </span>
                  )}
                </td>
                <td className="p-2">{elm?.email}</td>
                <td className="p-2">{elm?.phoneNumber}</td>
                <td className="p-2">{elm?.productTitle}</td>
                <td className="p-2 whitespace-nowrap">
                  {elm?.totalPaid != null
                    ? `${elm?.currency ? `${elm.currency} ` : ""}${Number(elm.totalPaid).toFixed(2)}`
                    : "—"}
                </td>
                <td className="p-2 font-mono font-bold text-aqua">{elm?.redeemCodeData?.redeemCode}</td>
                <td className="p-2">{elm?.redeemCodeData?.redeemCounter}</td>
                <td className="p-2">{elm?.totalParticipants}</td>
                <td className="p-2 whitespace-nowrap">
                  {/* The customer's own language, as found on the booking, with how sure we are. */}
                  <div className="flex items-center gap-1">
                    <span className="capitalize">{commLanguage || (elm?.languageStatus === "known" ? "—" : elm?.bookingLanguage || "—")}</span>
                    <LanguageStatusPill status={elm?.languageStatus} />
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    E-mail sent in <span className="capitalize">{elm?.bookingLanguage || "—"}</span>
                    {audioLanguage ? ` · audio ${audioLanguage}` : ""}
                  </div>
                </td>
                <td className="p-2 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <EmailStatusPill attempt={attempt} />
                    {canResend && (
                      <button
                        type="button"
                        disabled={isResending}
                        onClick={() => onResend(elm)}
                        className={`px-2 py-0.5 text-[11px] font-semibold rounded border ${isResending ? "border-gray-300 text-gray-400 cursor-wait" : "border-aqua text-aqua hover:bg-aqua hover:text-white"}`}
                        title="Send the tour-code e-mail again to this booking's address"
                      >
                        {isResending ? "Sending…" : "Resend"}
                      </button>
                    )}
                  </div>
                  {attempt && (
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {emailLanguage ? `sent in ${emailLanguage}` : ""}
                      {attempt.fallbackReason ? (
                        <span className="text-amber-700"> · English fallback: {String(attempt.fallbackReason).replace(/_/g, " ")}</span>
                      ) : null}
                      {attempt.attempts > 1 ? ` · attempt ${attempt.attempts}` : ""}
                    </div>
                  )}
                </td>
                <td className="p-2 whitespace-nowrap">{formatDate(elm?.bookingDate)}</td>
                <td className="p-2 whitespace-nowrap">{formatDate(elm?.createdAt)}</td>
                <td className="p-2">{elm?.notification || "—"}</td>
                <td className="p-2">
                  {isCancelled ? (
                    <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-700 rounded-full">Cancelled</span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-700 rounded-full">Active</span>
                  )}
                </td>
              </tr>
              );
            })}
        </tbody>
      </table>
      </div>
    </>
  );
};

export default BookingsTableComponent;
